import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import {
  type UseAdminMatchingFeedbacksApiOptions,
  useAdminMatchingFeedbacksApi,
  useAdminTenantsApi,
} from "hooks/useAdminApi";
import { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EMPTY_FILTERS,
  FeedbackFilters,
  type FeedbackFiltersValue,
} from "./components/FeedbackFilters";
import { FeedbackTable } from "./components/FeedbackTable";

type SortKey = NonNullable<UseAdminMatchingFeedbacksApiOptions["sort"]>;
type SortOrder = NonNullable<UseAdminMatchingFeedbacksApiOptions["order"]>;

const DEFAULT_LIMIT = 20;
const COMMENT_DEBOUNCE_MS = 300;

/**
 * マッチング評価一覧 (SYSTEM_ADMIN 専用) ページ。
 * 全テナント横断のフィードバック一覧、絞り込み、並び替え、ページネーションを提供する。
 * @return {JSX.Element} ページ
 */
export const MatchingFeedbacksPage: FC = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<FeedbackFiltersValue>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortKey>("created_at");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState<number>(1);

  // commentQuery は入力 1 文字ごとに API を叩かないようデバウンスする
  const [debouncedCommentQuery, setDebouncedCommentQuery] = useState<string>(
    filters.commentQuery,
  );
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedCommentQuery(filters.commentQuery);
      setPage(1);
    }, COMMENT_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filters.commentQuery]);

  const apiOptions = useMemo<UseAdminMatchingFeedbacksApiOptions>(
    () => ({
      tenantId: filters.tenantId || undefined,
      ratings: filters.ratings.length > 0 ? filters.ratings : undefined,
      createdAtFrom: filters.createdAtFrom || undefined,
      createdAtTo: filters.createdAtTo || undefined,
      commentQuery: debouncedCommentQuery.trim()
        ? debouncedCommentQuery.trim()
        : undefined,
      sort,
      order,
      page,
      limit: DEFAULT_LIMIT,
    }),
    [
      filters.tenantId,
      filters.ratings,
      filters.createdAtFrom,
      filters.createdAtTo,
      debouncedCommentQuery,
      sort,
      order,
      page,
    ],
  );

  const { data, loading, error } = useAdminMatchingFeedbacksApi(apiOptions);
  const { data: tenantsData, loading: tenantsLoading } = useAdminTenantsApi();

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / DEFAULT_LIMIT))
    : 1;
  const startIdx = data && data.total > 0 ? (page - 1) * DEFAULT_LIMIT + 1 : 0;
  const endIdx = data ? Math.min(data.total, page * DEFAULT_LIMIT) : 0;

  /**
   * フィルタを変更した際は 1 ページ目に戻す
   * @param {FeedbackFiltersValue} next 新しいフィルタ値
   */
  const handleFiltersChange = (next: FeedbackFiltersValue): void => {
    setFilters(next);
    setPage(1);
  };

  /**
   * フィルタをリセット
   */
  const handleReset = (): void => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  /**
   * ソート切替
   * @param {SortKey} nextSort 次のソートキー
   * @param {SortOrder} nextOrder 次のソート順
   */
  const handleSortChange = (nextSort: SortKey, nextOrder: SortOrder): void => {
    setSort(nextSort);
    setOrder(nextOrder);
    setPage(1);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-4">
        <PageTitle>マッチング評価一覧</PageTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
        >
          ← ダッシュボードに戻る
        </Button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        全テナントのマッチング結果に対するフィードバック（★1〜5
        と任意コメント）を横断表示します。件名クリックで該当メール詳細に遷移できます。
      </p>

      {error && (
        <div className="alert alert-error mb-4">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      )}

      <FeedbackFilters
        value={filters}
        tenants={tenantsData?.items ?? []}
        tenantsLoading={tenantsLoading}
        onChange={handleFiltersChange}
        onReset={handleReset}
      />

      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
        <span>
          全 {data?.total ?? 0} 件中 {startIdx}〜{endIdx} 件を表示
        </span>
        <span>
          並び替え: {sort === "created_at" && "評価日時"}
          {sort === "rating" && "★評価"}
          {sort === "total_score" && "マッチスコア"}{" "}
          {order === "desc" ? "降順" : "昇順"}
        </span>
      </div>

      <FeedbackTable
        items={data?.items ?? []}
        loading={loading}
        sort={sort}
        order={order}
        onSortChange={handleSortChange}
      />

      {/* ページネーション */}
      {data && data.total > DEFAULT_LIMIT && (
        <div className="mt-4 flex justify-center items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            « 前へ
          </Button>
          <span className="text-sm text-gray-600">
            {page} / {totalPages} ページ
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            次へ »
          </Button>
        </div>
      )}
    </div>
  );
};
